import React, { createContext, useContext, useState, useEffect } from "react";
import { apiClient } from "../services/apiClient";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (token) {
          // Set token in API client
          apiClient.setAuthToken(token);

          // Verify token and get user info
          const response = await apiClient.get("/api/auth/me");
          if (response.success) {
            setUser(response.data.user);
          } else {
            // Token is invalid, remove it
            localStorage.removeItem("authToken");
            apiClient.setAuthToken(null);
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        // Clean up invalid token
        localStorage.removeItem("authToken");
        apiClient.setAuthToken(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post("/api/auth/login", {
        username,
        password,
      });

      if (response.success) {
        const { user: userData, token } = response.data;

        // Store token
        localStorage.setItem("authToken", token);
        apiClient.setAuthToken(token);

        // Set user state
        setUser(userData);

        return { success: true };
      } else {
        throw new Error(response.message || "Login failed");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Login failed";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional - JWT is stateless)
      await apiClient.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      // Clean up local state regardless of API call result
      localStorage.removeItem("authToken");
      apiClient.setAuthToken(null);
      setUser(null);
      setError(null);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.post("/api/auth/register", userData);

      if (response.success) {
        const { user: newUser, token } = response.data;

        // Store token
        localStorage.setItem("authToken", token);
        apiClient.setAuthToken(token);

        // Set user state
        setUser(newUser);

        return { success: true };
      } else {
        throw new Error(response.message || "Registration failed");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Registration failed";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (updatedUser) => {
    setUser((prev) => ({ ...prev, ...updatedUser }));
  };

  const clearError = () => {
    setError(null);
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await apiClient.post("/api/auth/refresh", {
        refreshToken,
      });

      if (response.success) {
        const { token, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem("authToken", token);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }

        apiClient.setAuthToken(token);
        return true;
      }

      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      await logout();
      return false;
    }
  };

  const isAdmin = () => {
    return user?.role === "admin";
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    register,
    updateUser,
    clearError,
    refreshToken,
    isAdmin,
    hasRole,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
